<template>
  <form @submit.prevent="submit" ref="form">
    <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="swapSensorModal" @keyup.esc="close">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header header">
            <h3 class="modal-title">
              {{ $t('swapSensorModal.title') }}
              <span v-if="meteringPoint" class="modal-subtitle"> — {{ meteringPoint.name }}</span>
            </h3>
            <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-show="errorMessage" class="alert alert-danger failed">{{ errorMessage }}</div>

            <div v-if="!meteringPoint" class="small text-muted">
              {{ $t('swapSensorModal.loading') }}
            </div>
            <template v-else>
              <!-- current state summary -->
              <div class="form-group">
                <label class="small text-muted">{{ $t('swapSensorModal.currentSensorLabel') }}</label>
                <div class="blue-text">
                  <span v-if="currentSensorName">{{ currentSensorName }}</span>
                  <span v-else>—</span>
                </div>
              </div>

              <!-- swap timestamp (defaults to now; operator can backdate) -->
              <div class="form-group">
                <label for="validfromInput">{{ $t('swapSensorModal.validfromLabel') }}</label>
                <input type="datetime-local" class="form-control" id="validfromInput" v-model="validfromLocal" />
                <div class="small text-muted">{{ $t('swapSensorModal.validfromHint') }}</div>
              </div>

              <!-- outgoing endvalue (primary channel) -->
              <div class="form-group">
                <label for="endvalueInput">
                  {{ $t('swapSensorModal.endvalueLabel') }}<span v-if="hasSecondary && primaryObis"> &middot; {{ primaryObis }}</span>
                </label>
                <input type="text" inputmode="decimal" class="form-control" :class="{ 'is-invalid': dirty && !endvalueValid }" id="endvalueInput" v-model.trim="endvalue" :placeholder="$t('swapSensorModal.endvaluePlaceholder')" />
                <div class="invalid-feedback" v-if="dirty && !endvalueValid">{{ $t('swapSensorModal.valueDecimal') }}</div>
                <div class="small text-muted">{{ $t('swapSensorModal.endvalueHint') }}</div>
              </div>

              <!-- incoming firstvalue (primary channel) -->
              <div class="form-group">
                <label for="firstvalueInput">
                  {{ $t('swapSensorModal.firstvalueLabel') }}<span v-if="hasSecondary && primaryObis"> &middot; {{ primaryObis }}</span>
                </label>
                <input type="text" inputmode="decimal" class="form-control" :class="{ 'is-invalid': dirty && !firstvalueValid }" id="firstvalueInput" v-model.trim="firstvalue" :placeholder="$t('swapSensorModal.firstvaluePlaceholder')" />
                <div class="invalid-feedback" v-if="dirty && !firstvalueValid">{{ firstvalue ? $t('swapSensorModal.valueDecimal') : $t('swapSensorModal.firstvalueRequired') }}</div>
              </div>

              <!-- secondary channel (bi-directional meters: e.g. PV export) -->
              <template v-if="hasSecondary">
                <div class="form-group">
                  <label for="endvalue2Input">
                    {{ $t('swapSensorModal.endvalueLabel') }} &middot; {{ secondaryObis || $t('swapSensorModal.secondaryFallback') }}
                  </label>
                  <input type="text" inputmode="decimal" class="form-control" :class="{ 'is-invalid': dirty && !endvalue2Valid }" id="endvalue2Input" v-model.trim="endvalue2" :placeholder="$t('swapSensorModal.endvaluePlaceholder')" />
                  <div class="invalid-feedback" v-if="dirty && !endvalue2Valid">{{ $t('swapSensorModal.valueDecimal') }}</div>
                </div>
                <div class="form-group">
                  <label for="firstvalue2Input">
                    {{ $t('swapSensorModal.firstvalueLabel') }} &middot; {{ secondaryObis || $t('swapSensorModal.secondaryFallback') }}
                  </label>
                  <input type="text" inputmode="decimal" class="form-control" :class="{ 'is-invalid': dirty && !firstvalue2Valid }" id="firstvalue2Input" v-model.trim="firstvalue2" :placeholder="$t('swapSensorModal.firstvaluePlaceholder')" />
                  <div class="invalid-feedback" v-if="dirty && !firstvalue2Valid">{{ firstvalue2 ? $t('swapSensorModal.valueDecimal') : $t('swapSensorModal.firstvalueRequired') }}</div>
                </div>
              </template>

              <!-- new sensor selector (defaults to current = "only meter swapped") -->
              <div class="form-group">
                <label for="newSensorInput">{{ $t('swapSensorModal.newSensorLabel') }}</label>
                <select class="form-control" id="newSensorInput" v-model="newCdcSensorId">
                  <option v-for="s in candidateSensors" :key="s.id" :value="s.id">
                    {{ s.name }}<span v-if="s.id === meteringPoint.currentSensorId"> ({{ $t('swapSensorModal.sameSensorMarker') }})</span>
                  </option>
                </select>
                <div class="small text-muted">{{ $t('swapSensorModal.newSensorHint') }}</div>
              </div>

              <!-- swapnote -->
              <div class="form-group">
                <label for="swapnoteInput">{{ $t('swapSensorModal.swapnoteLabel') }}</label>
                <input type="text" class="form-control" id="swapnoteInput" v-model="swapnote" maxlength="1024" :placeholder="$t('swapSensorModal.swapnotePlaceholder')" />
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button type="submit" class="btn btn-primary" :disabled="!meteringPoint || saving">{{ $t('swapSensorModal.submit') }}</button>
            <button type="button" class="btn btn-secondary" @click="close">{{ $t('swapSensorModal.close') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { Modal } from 'bootstrap'
import ModalViewMixin from '@/mixins/ModalViewMixin.js'
import meteringpointsService from '@/services/meteringpoints'
import { isDecimalInput, parseDecimalInput } from '@/utils/decimal-input.js'

// OBIS hint for electricity import/export valuetypes (mirrors
// AddVirtualSensorValueModal). Frontend-only label sugar; the server
// only cares about the numeric valuetypes themselves.
const OBIS_FOR_VALUETYPE = {
  9: '1.8.0',
  10: '2.8.0'
}

export default {
  name: 'SwapSensorModal',
  mixins: [ModalViewMixin],
  props: {
    // ID of the meteringpoint to swap on. The modal fetches the full
    // record (incl. history + currentSensorId) when modalOpened changes.
    meteringPointId: { type: Number, default: null },
    // Candidate sensors for the new-sensor dropdown — typically the
    // facility's sensors of the same type as the meteringpoint. Phase-2
    // caller controls the filtering.
    candidateSensors: { type: Array, default: () => [] },
    // Current sensor object for the meteringpoint. Used to detect
    // bi-directional (sensor.json.secondaryValueType) and to label the
    // primary/secondary inputs.
    currentSensor: { type: Object, default: null },
    modalOpened: { type: String, required: true }
  },
  data () {
    return {
      meteringPoint: null,
      endvalue: null,
      firstvalue: null,
      endvalue2: null,
      firstvalue2: null,
      validfromLocal: '',
      primaryValueType: null,
      secondaryValueType: null,
      newCdcSensorId: null,
      swapnote: '',
      dirty: false,
      saving: false,
      errorMessage: ''
    }
  },
  computed: {
    firstvalueValid () {
      return this.firstvalue != null && this.firstvalue !== '' && isDecimalInput(this.firstvalue)
    },
    firstvalue2Valid () {
      return this.firstvalue2 != null && this.firstvalue2 !== '' && isDecimalInput(this.firstvalue2)
    },
    endvalueValid () {
      return isDecimalInput(this.endvalue)
    },
    endvalue2Valid () {
      return isDecimalInput(this.endvalue2)
    },
    currentSensorName () {
      return this.meteringPoint && this.meteringPoint.currentSensorName
    },
    hasSecondary () {
      return typeof this.secondaryValueType === 'number'
    },
    primaryObis () {
      return OBIS_FOR_VALUETYPE[this.primaryValueType] || null
    },
    secondaryObis () {
      return OBIS_FOR_VALUETYPE[this.secondaryValueType] || null
    }
  },
  watch: {
    modalOpened () {
      this.meteringPoint = null
      this.endvalue = null
      this.firstvalue = null
      this.endvalue2 = null
      this.firstvalue2 = null
      this.validfromLocal = this.nowAsLocalDateTimeInput()
      this.swapnote = ''
      this.dirty = false
      this.saving = false
      this.errorMessage = ''
      const meta = this.parseSensorJson(this.currentSensor)
      this.primaryValueType = (typeof meta.primaryValueType === 'number') ? meta.primaryValueType : null
      this.secondaryValueType = (typeof meta.secondaryValueType === 'number') ? meta.secondaryValueType : null
      if (this.meteringPointId == null) return
      meteringpointsService.getMeteringPoint(this.meteringPointId)
        .then((data) => {
          this.meteringPoint = data && data.meteringpoint
          this.newCdcSensorId = this.meteringPoint ? this.meteringPoint.currentSensorId : null
        })
        .catch((error) => {
          this.errorMessage = this.localizedBackendMessage ? this.localizedBackendMessage(error.message) : (error.message || 'Failed')
        })
    }
  },
  methods: {
    parseSensorJson (sensor) {
      if (!sensor || !sensor.json || typeof sensor.json !== 'string') return {}
      try {
        const parsed = JSON.parse(sensor.json)
        return (parsed && typeof parsed === 'object') ? parsed : {}
      } catch (e) {
        return {}
      }
    },
    // Format "now" into the value shape <input type="datetime-local"> expects:
    // YYYY-MM-DDTHH:mm in the browser's local time zone.
    nowAsLocalDateTimeInput () {
      const d = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    submit () {
      this.dirty = true
      if (!this.firstvalueValid || !this.endvalueValid) return
      if (this.hasSecondary && (!this.firstvalue2Valid || !this.endvalue2Valid)) return
      if (this.newCdcSensorId == null) return
      this.saving = true
      this.errorMessage = ''
      const payload = {
        cdcsensorid: this.newCdcSensorId,
        firstvalue: parseDecimalInput(this.firstvalue),
        endvalue: parseDecimalInput(this.endvalue),
        swapnote: this.swapnote || null
      }
      if (this.hasSecondary) {
        payload.firstvalue2 = parseDecimalInput(this.firstvalue2)
        payload.endvalue2 = parseDecimalInput(this.endvalue2)
      }
      // Backdate: include validfrom as ISO-8601 (UTC). When the
      // operator leaves the field at its default ("now") the backend
      // treats null and now() identically, but sending the explicit
      // timestamp is harmless and keeps the API consistent.
      if (this.validfromLocal) {
        const parsed = new Date(this.validfromLocal)
        if (!isNaN(parsed.getTime())) {
          payload.validfrom = parsed.toISOString()
        }
      }
      meteringpointsService.swap(this.meteringPointId, payload)
        .then(() => {
          this.$emit('swapped')
          this.close()
        })
        .catch((error) => {
          this.saving = false
          this.errorMessage = this.localizedBackendMessage ? this.localizedBackendMessage(error.message) : (error.message || 'Failed')
        })
    },
    close () {
      this.emitClosed && this.emitClosed()
      Modal.getInstance(document.getElementById('swapSensorModal'))?.hide()
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-subtitle { font-weight: 400; font-size: 0.9em; opacity: 0.85; }
.blue-text { color: #0069d9; font-weight: 700; }
</style>
